import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Scrolls a native `<select multiple>` so its first selected option is brought into view.
 *
 * Useful when a list has many options and the pre-selected item(s) may be far down the list;
 * without this the user sees the top of the list and cannot tell where the selection begins.
 *
 * Bind the directive to a value that changes when the option list is (re)loaded - typically the
 * options array itself. That arms the directive; the actual scroll happens once the bound
 * ngModel/form value has been applied to the control (which is when the value accessor updates the
 * `<option>` selected state in the DOM). Because NgModel applies its initial value asynchronously,
 * listening to the control's value is more reliable than the options change alone.
 *
 * If the select is populated while hidden (e.g. inside a collapsed panel), it has no layout and the
 * scroll cannot be computed. A ResizeObserver detects when the element becomes visible (gains a box)
 * and, while still armed, performs the scroll then - so opening the panel later still works.
 *
 * After a successful scroll the directive disarms, so ordinary user selections do not cause the
 * list to jump; it re-arms the next time the options list changes.
 *
 * Example:
 *   <select multiple [ngModel]="selectedIds" [scrollToFirstSelected]="options">
 *     ...
 *   </select>
 */
@Directive({
    selector: 'select[scrollToFirstSelected]',
    standalone: true,
})
export class ScrollToFirstSelectedDirective implements OnInit, OnDestroy {
    constructor(
        private readonly _el: ElementRef<HTMLSelectElement>,
        private readonly _zone: NgZone,
        @Optional() @Self() private readonly _ngControl: NgControl,
    ) {}

    private static readonly MAX_ATTEMPTS = 30;

    private _rafId: number | null = null;
    private _valueSub: Subscription | null = null;
    private _resizeObserver: ResizeObserver | null = null;
    private _armed = false;

    // Re-arm whenever the bound value changes (e.g. the options list finished loading).
    @Input('scrollToFirstSelected') set trigger(_value: unknown) {
        this._armed = true;
        this._schedule();
    }

    ngOnInit(): void {
        // Re-attempt the scroll whenever the ngModel/form value is applied to the control - this is
        // the moment the value accessor sets the <option> selected state (including NgModel's async
        // initial write). Guarded by `_armed` so only list (re)loads trigger an auto-scroll, not
        // ordinary user selections.
        const control = this._ngControl?.control;
        if (control) {
            this._valueSub = control.valueChanges.subscribe(() => {
                if (this._armed) {
                    this._schedule();
                }
            });
        }

        // Detect when the select becomes visible (gains a layout box) - e.g. when a collapsed panel
        // is opened after the data was populated while hidden - and scroll then, while still armed.
        if (typeof ResizeObserver !== 'undefined') {
            this._zone.runOutsideAngular(() => {
                this._resizeObserver = new ResizeObserver(() => {
                    if (this._armed && this._isVisible()) {
                        this._schedule();
                    }
                });
                this._resizeObserver.observe(this._el.nativeElement);
            });
        }
    }

    ngOnDestroy(): void {
        this._cancel();
        this._valueSub?.unsubscribe();
        this._resizeObserver?.disconnect();
    }

    private _schedule(): void {
        this._cancel();

        let attempts = 0;

        // Run the retry loop outside Angular so it doesn't trigger change detection each frame and
        // isn't dependent on Angular's change-detection cycles to keep firing.
        this._zone.runOutsideAngular(() => {
            const attempt = (): void => {
                this._rafId = null;

                if (this._scrollToFirstSelected()) {
                    // Scrolled successfully; stop until the list is (re)loaded so user selections
                    // don't cause the list to jump.
                    this._armed = false;
                    return;
                }

                if (++attempts >= ScrollToFirstSelectedDirective.MAX_ATTEMPTS) {
                    return;
                }

                this._rafId = requestAnimationFrame(attempt);
            };

            this._rafId = requestAnimationFrame(attempt);
        });
    }

    private _cancel(): void {
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    private _isVisible(): boolean {
        const el = this._el.nativeElement;
        // offsetParent is null for display:none elements; also require a real box height.
        return el.offsetParent !== null && el.getBoundingClientRect().height > 0;
    }

    private _scrollToFirstSelected(): boolean {
        const select = this._el.nativeElement;
        const options = select.options;

        if (!options || options.length === 0) {
            return false;
        }

        let firstSelectedIndex = -1;
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                firstSelectedIndex = i;
                break;
            }
        }

        // Nothing selected yet - keep waiting (selection may be applied on a later frame).
        if (firstSelectedIndex === -1) {
            return false;
        }

        // The first option is already selected/at the top; nothing to scroll.
        if (firstSelectedIndex === 0) {
            select.scrollTop = 0;
            return true;
        }

        const selectRect = select.getBoundingClientRect();

        // The select has no layout yet (e.g. still hidden) - retry on a later frame.
        if (selectRect.height === 0) {
            return false;
        }

        // Measure relative to the select's own visible box (independent of offsetParent), so the
        // result is consistent across every select regardless of surrounding positioned ancestors.
        const optionRect = options[firstSelectedIndex].getBoundingClientRect();
        const contextOffset = optionRect.height; // leave ~one row of context above the selection

        // Delta from the top of the select's viewport to the option, applied on top of the current
        // scroll position, brings the first selected option to a consistent position near the top.
        select.scrollTop += optionRect.top - selectRect.top - contextOffset;
        return true;
    }
}

